/**
 * Wandelt eine Szene in SVG um.
 *
 * Die Ausgabe ist eine Zeichenkette ohne Zufall: gleiche Szene, gleiche
 * Zeichenkette. Farben werden erst hier aus der Palette eingesetzt, so dass
 * dieselbe Szene im Hell- und Dunkelmodus funktioniert.
 */
import { fmt } from './geometry.ts';
import { CANVAS, FONT_FAMILY, LIGHT_PALETTE, STROKE } from './palette.ts';
import type { Palette, Tone } from './palette.ts';
import type { GroupNode, Scene, SceneNode, Style, Transform } from './scene.ts';

/** Einstellungen beim Zeichnen. */
export interface RenderOptions {
  readonly palette?: Palette;
  /** Text für Screenreader. */
  readonly title?: string;
  /** Hintergrundfläche zeichnen (Standard: ja). */
  readonly background?: boolean;
  /** Vorsilbe für Kennungen von Beschnittpfaden, falls mehrere Bilder auf einer Seite stehen. */
  readonly idPrefix?: string;
  /** Feste Breite; ohne Angabe skaliert das Bild mit dem Behälter. */
  readonly width?: number;
}

/** Zustand während des Zeichnens. */
interface RenderContext {
  readonly palette: Palette;
  readonly prefix: string;
  readonly defs: string[];
  clipCount: number;
}

/** Farbe einer Tonstufe. */
function color(tone: Tone | undefined, palette: Palette, fallback: string): string {
  if (tone === undefined) {
    return fallback;
  }
  return tone === 'none' ? 'none' : palette[tone];
}

/** Deckkraft-Attribut, nur wenn nötig. */
function opacityAttribute(opacity: number | undefined): string {
  return opacity !== undefined && opacity < 0.999 ? ` opacity="${fmt(Math.max(0, opacity))}"` : '';
}

/** Zeichnet die gemeinsamen Darstellungsattribute. */
function styleAttributes(style: Style, palette: Palette): string {
  const fill = color(style.fill, palette, 'none');
  const stroke = color(style.stroke, palette, palette.ink);
  const parts = [` fill="${fill}"`];
  if (stroke === 'none') {
    parts.push(' stroke="none"');
  } else {
    parts.push(` stroke="${stroke}" stroke-width="${fmt(style.strokeWidth ?? STROKE.outline)}"`);
    if (style.cap !== undefined) {
      parts.push(` stroke-linecap="${style.cap}"`);
    }
    if (style.dash !== undefined) {
      parts.push(` stroke-dasharray="${style.dash}"`);
    }
  }
  parts.push(opacityAttribute(style.opacity));
  return parts.join('');
}

/** Baut das transform-Attribut. */
function transformAttribute(transform: Transform | undefined): string {
  if (transform === undefined) {
    return '';
  }
  const parts: string[] = [];
  const ox = transform.ox ?? 0;
  const oy = transform.oy ?? 0;
  if ((transform.x ?? 0) !== 0 || (transform.y ?? 0) !== 0) {
    parts.push(`translate(${fmt(transform.x ?? 0)} ${fmt(transform.y ?? 0)})`);
  }
  if (transform.rotate !== undefined && Math.abs(transform.rotate) > 0.001) {
    parts.push(`rotate(${fmt(transform.rotate)} ${fmt(ox)} ${fmt(oy)})`);
  }
  const sx = transform.scaleX ?? transform.scale ?? 1;
  const sy = transform.scaleY ?? transform.scale ?? 1;
  if (Math.abs(sx - 1) > 0.001 || Math.abs(sy - 1) > 0.001) {
    parts.push(
      `translate(${fmt(ox)} ${fmt(oy)}) scale(${fmt(sx)} ${fmt(sy)}) translate(${fmt(-ox)} ${fmt(-oy)})`,
    );
  }
  return parts.length === 0 ? '' : ` transform="${parts.join(' ')}"`;
}

/** Ersetzt Sonderzeichen in Texten. */
export function escapeText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Zeichnet eine Gruppe. */
function renderGroup(node: GroupNode, context: RenderContext): string {
  const inner = node.children.map((child) => renderNode(child, context)).join('');
  const transform = transformAttribute(node.transform);
  const opacity = opacityAttribute(node.opacity);
  if (node.clip === undefined) {
    return `<g${transform}${opacity}>${inner}</g>`;
  }
  // Der Beschnitt liegt in den Koordinaten der Gruppe, also nach ihrer Umformung.
  context.clipCount += 1;
  const id = `${context.prefix}-clip-${context.clipCount}`;
  context.defs.push(`<clipPath id="${id}"><path d="${node.clip}"/></clipPath>`);
  return `<g${transform}${opacity}><g clip-path="url(#${id})">${inner}</g></g>`;
}

/** Zeichnet einen Knoten. */
function renderNode(node: SceneNode, context: RenderContext): string {
  const { palette } = context;
  switch (node.kind) {
    case 'path':
      return `<path d="${node.d}"${styleAttributes(node, palette)}/>`;
    case 'circle':
      return `<circle cx="${fmt(node.cx)}" cy="${fmt(node.cy)}" r="${fmt(Math.max(0, node.r))}"${styleAttributes(node, palette)}/>`;
    case 'ellipse':
      return `<ellipse cx="${fmt(node.cx)}" cy="${fmt(node.cy)}" rx="${fmt(Math.max(0, node.rx))}" ry="${fmt(Math.max(0, node.ry))}"${styleAttributes(node, palette)}/>`;
    case 'rect': {
      const radius = node.r === undefined || node.r === 0 ? '' : ` rx="${fmt(node.r)}"`;
      return `<rect x="${fmt(node.x)}" y="${fmt(node.y)}" width="${fmt(Math.max(0, node.width))}" height="${fmt(Math.max(0, node.height))}"${radius}${styleAttributes(node, palette)}/>`;
    }
    case 'line':
      return `<line x1="${fmt(node.x1)}" y1="${fmt(node.y1)}" x2="${fmt(node.x2)}" y2="${fmt(node.y2)}"${styleAttributes(node, palette)}/>`;
    case 'text':
      return `<text x="${fmt(node.x)}" y="${fmt(node.y)}" font-size="${fmt(node.size)}" font-weight="${node.weight ?? 700}" text-anchor="${node.anchor ?? 'middle'}" fill="${color(node.fill, palette, palette.ink)}" stroke="none"${opacityAttribute(node.opacity)}>${escapeText(node.text)}</text>`;
    case 'group':
      return renderGroup(node, context);
    default:
      return '';
  }
}

/** Zeichnet nur den Inhalt einer Szene, ohne äußeres svg-Element. */
export function renderBody(scene: Scene, options: RenderOptions = {}): string {
  const palette = options.palette ?? LIGHT_PALETTE;
  const context: RenderContext = { palette, prefix: options.idPrefix ?? 'ka', defs: [], clipCount: 0 };
  const body = scene.map((node) => renderNode(node, context)).join('');
  const background =
    options.background === false
      ? ''
      : `<rect width="${CANVAS.width}" height="${CANVAS.height}" fill="${palette.backdrop}" stroke="none"/>`;
  const defs = context.defs.length === 0 ? '' : `<defs>${context.defs.join('')}</defs>`;
  return `${defs}${background}${body}`;
}

/** Vollständiges SVG. */
export function renderSvg(scene: Scene, options: RenderOptions = {}): string {
  const size =
    options.width === undefined
      ? ''
      : ` width="${fmt(options.width)}" height="${fmt((options.width * CANVAS.height) / CANVAS.width)}"`;
  const title = options.title === undefined ? '' : ` role="img" aria-label="${escapeText(options.title)}"`;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CANVAS.width} ${CANVAS.height}"${size}`,
    ` stroke-linecap="round" stroke-linejoin="round" font-family="${FONT_FAMILY.replace(/"/g, "'")}"${title}>`,
    renderBody(scene, options),
    '</svg>',
  ].join('');
}
